import { useEffect, useMemo, useState } from 'react';

import { Combobox } from '@apps/web/components/ui/combobox';
import { CommandEmpty, CommandItem } from '@apps/web/components/ui/command';
import { cn } from '@apps/web/lib/utils';
import { type HHAExchangeModifyReasonItem } from '@libraries/typescript/api_client';

type HHAExchangeModifyReasonSelectorProps = {
    readonly reasons: Array<HHAExchangeModifyReasonItem> | undefined;
    readonly selectedReasonId: number | null;
    readonly defaultReasonId: number | null | undefined;
    readonly isLoading?: boolean;
    readonly error?: unknown;
    readonly disabled?: boolean;
    readonly onReasonChange: (reasonId: number | null) => void;
    readonly hasError?: boolean;
};

export function HHAExchangeModifyReasonSelector({
    reasons,
    selectedReasonId,
    defaultReasonId,
    isLoading,
    error,
    disabled,
    onReasonChange,
    hasError,
}: HHAExchangeModifyReasonSelectorProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');

    const reasonList = useMemo(() => reasons ?? [], [reasons]);

    const selectedReason = useMemo(
        () => reasonList.find((r) => r.id === selectedReasonId) ?? null,
        [reasonList, selectedReasonId],
    );

    useEffect(() => {
        if (
            selectedReasonId === null &&
            defaultReasonId !== null &&
            defaultReasonId !== undefined &&
            reasonList.length > 0
        ) {
            onReasonChange(defaultReasonId);
        }
    }, [defaultReasonId, selectedReasonId, reasonList.length, onReasonChange]);

    return (
        <div className="flex min-w-0 flex-1 flex-col gap-1 text-xs">
            <span className="text-muted-foreground text-xs font-medium">
                Modify Reason
            </span>
            <Combobox
                open={isOpen}
                onOpenChange={setIsOpen}
                displayValue={selectedReason?.name}
                placeholder={
                    isLoading
                        ? 'Loading reasons...'
                        : error
                          ? 'Error loading reasons'
                          : reasonList.length === 0
                            ? 'No reasons available'
                            : 'Select modify reason'
                }
                disabled={disabled || isLoading || reasonList.length === 0}
                searchValue={search}
                onSearchValueChange={setSearch}
                searchPlaceholder="Search modify reasons..."
                triggerClassName={cn(
                    'text-left overflow-hidden',
                    hasError &&
                        'border-destructive focus-visible:ring-destructive',
                )}
                contentClassName="w-72 p-1"
                contentSide="bottom"
                contentSideOffset={8}
                commandListProps={{
                    className: 'max-h-64 overflow-y-auto overscroll-contain',
                    onWheel: (e) => {
                        e.stopPropagation();
                        const target = e.currentTarget;
                        target.scrollTop += e.deltaY;
                    },
                }}
            >
                <CommandEmpty className="text-muted-foreground py-6 text-center text-xs">
                    No modify reasons found
                </CommandEmpty>
                {reasonList.map((reason) => (
                    <CommandItem
                        key={reason.id}
                        value={reason.name}
                        onSelect={() => {
                            setIsOpen(false);
                            onReasonChange(reason.id);
                        }}
                        className="py-2 text-left text-sm"
                    >
                        {reason.name}
                    </CommandItem>
                ))}
            </Combobox>
            {hasError ? (
                <p className="text-destructive text-xs">
                    Select a modify reason to assign this shift.
                </p>
            ) : null}
        </div>
    );
}
